import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Award } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { RecordShell } from "@/components/records/RecordShell";
import { Progress } from "@/components/ui/progress";

const Certificate = ({ d }) => (
  <div className="mt-8 rounded-[1.5rem] border-[6px] border-double border-[#C87D53] p-10 text-center bg-[#FDFBF7]" data-testid="diploma-certificate">
    <Award className="w-12 h-12 mx-auto text-[#C87D53]" />
    <p className="text-xs uppercase tracking-[0.3em] text-stone-500 mt-4">ConsciousLearn · {d.edition}</p>
    <p className="font-display text-4xl text-[#2C4A3E] mt-4">High School Diploma</p>
    <p className="mt-6 text-stone-600">This certifies that</p>
    <p className="font-display text-5xl text-[#2C4A3E] mt-3" data-testid="diploma-name">{d.student.name}</p>
    <p className="mt-6 text-stone-600 max-w-xl mx-auto">has satisfactorily completed the course of study prescribed for graduation{d.issued.override ? "" : `, earning ${d.issued.credits} credits,`} and is awarded this diploma with all its honors and privileges.</p>
    <p className="mt-6 text-sm text-stone-500">Graduation date: <b data-testid="diploma-date">{d.issued.graduation_date}</b> · Diploma No. <b data-testid="diploma-number">{d.issued.number}</b></p>
    <div className="mt-12 grid grid-cols-2 gap-10 text-sm text-stone-600"><p className="border-t border-stone-400 pt-2">Administrator</p><p className="border-t border-stone-400 pt-2">Parent / Teacher ({d.issued.issued_by})</p></div>
  </div>
);

export default function Diploma() {
  const { studentId } = useParams();
  const { profile } = useProfile();
  const [d, setD] = useState(null);
  const [gd, setGd] = useState(new Date().toISOString().slice(0, 10));
  const load = () => http.get(`/students/${studentId}/diploma`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [studentId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!d) return <div className="py-20 text-center text-stone-500">Loading…</div>;
  const a = d.audit;
  const canIssue = ["teacher", "parent", "admin"].includes(profile?.role) && (a.eligible || profile.role === "admin");
  const issue = async () => { try { await http.post(`/students/${studentId}/diploma`, { graduation_date: gd, override: !a.eligible }); toast.success("Diploma issued"); load(); } catch (e) { toast.error(formatApiError(e)); } };
  return (
    <RecordShell title="Diploma" back testId="diploma-page">
      {d.issued ? <Certificate d={d} /> : (
        <div className="mt-8" data-testid="diploma-audit">
          <p className="font-display text-2xl text-[#2C4A3E]">Graduation credit audit · {d.student.name}</p>
          <div className="flex items-center gap-3 mt-4 max-w-md"><Progress value={(100 * a.credits_earned) / a.credits_required} className="h-2.5" />
            <span className="text-sm whitespace-nowrap" data-testid="diploma-credits">{a.credits_earned} / {a.credits_required} credits</span></div>
          <table className="w-full mt-6 text-sm"><tbody>{a.requirements.map((r) => (
            <tr key={r.area} className="border-b border-[#E6DEC3]"><td className="py-2">{r.area}</td><td className="py-2 text-right">{r.earned} / {r.required}</td></tr>
          ))}</tbody></table>
          <p className={`mt-4 text-sm ${a.eligible ? "text-[#2C4A3E]" : "text-[#C87D53]"}`} data-testid="diploma-eligibility">{a.eligible ? "All graduation credits earned. Ready to issue the diploma." : `${d.student.name} needs ${a.credits_required - a.credits_earned} more credits to graduate.`}</p>
          {canIssue && (
            <div className="mt-6 flex flex-wrap items-center gap-3 no-print">
              <input type="date" value={gd} onChange={(e) => setGd(e.target.value)} data-testid="diploma-date-input" className="px-4 py-2.5 rounded-xl bg-white border border-[#E6DEC3] text-sm" />
              <button onClick={issue} data-testid="diploma-issue-btn" className="px-6 py-2.5 rounded-full bg-[#C87D53] text-white text-sm">{a.eligible ? "Issue diploma" : "Issue with admin override"}</button>
            </div>
          )}
        </div>
      )}
    </RecordShell>
  );
}
